import { useEffect, useRef, useState } from "react";
import { IonButton, IonIcon, IonModal, IonToast } from "@ionic/react";
import { cartOutline, checkmarkCircle } from "ionicons/icons";
import { useHistory, useLocation } from "react-router";
import { useCart } from "./useCart";
import { useCommon } from "../../common/useCommon";
import { convertPrice } from "../../common/currencyUtils";
import { BIH_WARNING_ORDER_PRICE_KM } from "../../common/constants";
import { BihPriceWarning } from "../../components/BihPriceWarning/BihPriceWarning";

// Module-level flag so only one hook instance shows the price warning at a time
let priceWarningActive = false;

export const useCartNotification = () => {
  const history = useHistory();
  const location = useLocation();
  const { notifyCart, notifyCartModal, hideNotificationCart, priceTotal } =
    useCart();
  const { currency, conversionRate } = useCommon();

  const isCartPage = location.pathname.startsWith("/korpa");

  const priceTotalKM =
    currency === "KM" && conversionRate > 0
      ? convertPrice(priceTotal(), "KM", conversionRate)
      : 0;
  const formattedPriceKM = priceTotalKM.toFixed(2).replace(".", ",") + " KM";

  const prevTotalRef = useRef<number | null>(null);
  const [showPriceWarning, setShowPriceWarning] = useState(false);

  useEffect(() => {
    const prevTotal = prevTotalRef.current;
    prevTotalRef.current = priceTotalKM;

    if (prevTotal === null) return; // skip initial load

    if (
      currency === "KM" &&
      !isCartPage &&
      !priceWarningActive &&
      priceTotalKM >= BIH_WARNING_ORDER_PRICE_KM &&
      priceTotalKM > prevTotal
    ) {
      priceWarningActive = true;
      setShowPriceWarning(true);
    }
  }, [priceTotalKM, currency, isCartPage]);

  useEffect(() => {
    if (isCartPage && (notifyCart || notifyCartModal)) {
      hideNotificationCart();
    }
  }, [isCartPage, notifyCart, notifyCartModal, hideNotificationCart]);
  const modalPages = ["/", "/slike", "/omiljene", "/ram"];
  const isModalPage = modalPages.some((p) =>
    p === "/" ? location.pathname === "/" : location.pathname.startsWith(p),
  );
  const showModal =
    !isCartPage && isModalPage && (notifyCartModal || notifyCart);
  const showToast =
    !isCartPage && !showModal && (notifyCart || notifyCartModal);

  const cartNotification = (
    <>
      <IonToast
        isOpen={showToast}
        color="success"
        onDidDismiss={hideNotificationCart}
        message="Korpa za naručivanje ažurirana."
        position="bottom"
        duration={5000}
        cssClass="toast-cart"
        buttons={[{ text: "OK", role: "cancel" }]}
      />
      <IonModal
        isOpen={showModal}
        onDidDismiss={hideNotificationCart}
        className="cart-modal"
      >
        <div style={{ padding: "24px", textAlign: "center" }}>
          <IonIcon
            icon={checkmarkCircle}
            color="success"
            style={{ fontSize: 48 }}
          />
          <h2 style={{ margin: "12px 0" }}>Proizvod dodat u korpu!</h2>
          <IonButton
            expand="block"
            color="success"
            style={{ marginTop: 16, height: 48 }}
            onClick={() => {
              hideNotificationCart();
              history.push("/korpa");
            }}
          >
            <IonIcon icon={cartOutline} size="large" slot="start" />
            Kreiraj porudžbinu
          </IonButton>
          <IonButton
            expand="block"
            fill="outline"
            color="medium"
            style={{ marginTop: 10, height: 48 }}
            onClick={hideNotificationCart}
          >
            Nastavi kupovinu
          </IonButton>
        </div>
      </IonModal>
      <BihPriceWarning
        isOpen={showPriceWarning}
        formattedPriceKM={formattedPriceKM}
        priceTotalKM={priceTotalKM}
        onDismiss={() => {
          priceWarningActive = false;
          setShowPriceWarning(false);
        }}
      />
    </>
  );

  return { cartNotification };
};
